import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n } from '../../i18n/index.jsx';
import { formatDate } from '../../format.js';
import { Alert } from '../../components/ui.jsx';

const usd = (cents, lang) => new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'fr-CA', { style: 'currency', currency: 'USD', minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);

// Current plan, review status, today's limits, and the plan comparison with
// the Upgrade button (enabled once the company form is complete).
export default function MembershipTab({ membership: m, reload }) {
  const { t, lang, errorText } = useI18n();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ kind: '', text: '' });
  if (!m) return <p className="muted">{t('common.loading')}</p>;

  const name = (p) => (lang === 'en' ? p.nameEn : p.nameFr);
  const features = (p) => (lang === 'en' ? p.featuresEn : p.featuresFr);
  const current = m.plans.find((p) => p.code === m.plan);

  async function apply() {
    setBusy(true); setMsg({ kind: '', text: '' });
    try {
      await api('/membership/apply', { method: 'POST' });
      setMsg({ kind: 'success', text: t('membership.applied') });
      await reload();
    } catch (err) {
      setMsg({ kind: 'error', text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  let review = null;
  if (m.sellerStatus === 'pending') review = { kind: 'pending', text: t('membership.review.pending') };
  else if (m.sellerStatus === 'rejected') review = { kind: 'rejected', text: t('membership.review.rejected', { reason: m.sellerRejectionReason || '' }) };
  else if (m.sellerStatus === 'approved' && m.plan !== 'seller') review = { kind: 'approved', text: t('membership.review.approvedNotActive') };

  return (
    <div className="tab-body">
      <section className="card status-card">
        <div className="status-grid">
          <div>
            <p className="card-title">{t('membership.currentPlan')}</p>
            <p className="status-plan">{name(current)}</p>
            {m.complimentary ? (
              <p className="small"><span className="badge badge-free">{t('freeAccess.badge')}</span>{' '}
                {m.complimentary.until ? t('membership.freeUntil', { date: formatDate(m.complimentary.until, lang) }) : t('membership.freePermanent')}</p>
            ) : m.plan === 'seller' && m.planSource === 'grandfathered' && <p className="muted small">{t('membership.grandfathered')}</p>}
            {!m.complimentary && m.plan === 'seller' && m.planSource === 'manual' && (
              <p className="muted small">{m.planUntil ? t('membership.manualUntil', { date: formatDate(m.planUntil, lang) }) : t('membership.manual')}</p>
            )}
          </div>
          <div>
            <p className="card-title">{t('membership.today')}</p>
            {m.inquiries.limit === null
              ? <p className="status-plan">{t('membership.unlimited')}</p>
              : <>
                  <p className="status-plan">{t('membership.inquiriesLeft', { n: m.inquiries.remaining })}</p>
                  <div className="meter" aria-hidden="true"><span style={{ width: `${Math.min(100, (m.inquiries.used / Math.max(1, m.inquiries.limit)) * 100)}%` }} /></div>
                  <p className="muted small">{t('membership.inquiriesUsed', { used: m.inquiries.used, limit: m.inquiries.limit })}</p>
                </>}
          </div>
        </div>
        {review && <div className={`review-banner review-${review.kind}`}>{review.text}</div>}
      </section>

      <h2 className="tab-title">{t('membership.plansTitle')}</h2>
      <div className="plan-grid">
        {m.plans.map((p) => {
          const isCurrent = p.code === m.plan;
          return (
            <article key={p.code} className={`plan-card${p.code === 'seller' ? ' plan-card-featured' : ''}${isCurrent ? ' is-current' : ''}`}>
              {p.code === 'seller' && <span className="plan-ribbon">{t('membership.recommended')}</span>}
              <h3>{name(p)}</h3>
              <p className="plan-price">
                {p.priceUsdCents ? <>{usd(p.priceUsdCents, lang)}<span> {t('membership.perMonth')}</span></> : t('membership.free')}
              </p>
              <p className="muted small">{t('membership.inquiriesPerDay', { n: p.inquiriesPerDay })}</p>
              <ul className="plan-features">{features(p).map((f) => <li key={f}>{f}</li>)}</ul>
              {isCurrent ? (
                <span className="btn btn-ghost btn-block is-disabled">{t('membership.yourPlan')}</span>
              ) : p.canSell && user.role !== 'admin' ? (
                <UpgradeAction m={m} busy={busy} onApply={apply} />
              ) : null}
            </article>
          );
        })}
      </div>
      <Alert kind={msg.kind || 'error'}>{msg.text}</Alert>
      <p className="muted small">{t('membership.priceNote')}</p>
    </div>
  );
}

function UpgradeAction({ m, busy, onApply }) {
  const { t } = useI18n();
  if (m.sellerStatus === 'pending') return <span className="btn btn-ghost btn-block is-disabled">{t('membership.underReview')}</span>;
  if (!m.company.complete) {
    return (
      <>
        <button type="button" className="btn btn-primary btn-block" disabled>{t('membership.upgrade')}</button>
        <p className="small upgrade-hint">{t('membership.completeFirst')} <Link to="/account/company">{t('membership.goToCompany')}</Link></p>
      </>
    );
  }
  return (
    <>
      <button type="button" className="btn btn-primary btn-block" onClick={onApply} disabled={busy}>{busy ? t('common.sending') : t('membership.upgrade')}</button>
      <p className="small upgrade-hint muted">{t('membership.paymentSoon')}</p>
    </>
  );
}
